import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const apiClient = axios.create({
    baseURL: API_BASE_URL,
});

apiClient.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

const statusBadge = (status) => {
    switch (status) {
        case 'open': return <span className="badge bg-primary">Open</span>;
        case 'follow_up': return <span className="badge bg-warning text-dark">Follow-up</span>;
        case 'closed': return <span className="badge bg-secondary">Closed</span>;
        case 'converted': return <span className="badge bg-success">Converted</span>;
        default: return <span className="badge bg-info text-capitalize">{status}</span>;
    }
};

function LeadDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [lead, setLead] = useState(null);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [newStatus, setNewStatus] = useState('open');
    const [newNote, setNewNote] = useState('');

    const fetchLeadDetail = async () => {
        try {
            setLoading(true);
            const response = await apiClient.get(`/vendors/leads/${id}`);

            console.log(response);
            if (response.data.success) {
                setLead(response.data.data);
                setNewStatus(response.data.data.VendorLeads[0]?.status || 'open');
            }
        } catch (err) {
            toast.error("Failed to fetch lead details");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLeadDetail();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleStatusUpdate = async (e) => {
        e.preventDefault();
        try {
            setUpdating(true);
            const response = await apiClient.post(`/vendors/leads/${id}/status`, {
                status: newStatus,
                note: newNote
            });
            if (response.data.success) {
                toast.success("Status updated successfully");
                setNewNote('');
                fetchLeadDetail(); // Refresh history and current status
            }
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to update status");
        } finally {
            setUpdating(false);
        }
    };

    if (loading) return <div className="text-center p-5"><div className="spinner-border text-primary" role="status"></div></div>;
    if (!lead) return <div className="alert alert-danger m-3">Lead not found or unauthorized access.</div>;

    const vendorLead = lead.VendorLeads[0] || {};
    const isLocked = vendorLead.is_unlocked === false;

    const handleUnlock = async () => {
        Swal.fire({
            title: 'Unlock Lead?',
            text: "This will consume 1 lead balance from your account.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#08a88a',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, unlock it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await apiClient.post(`/vendors/leads/${id}/unlock`);
                    if (response.data.success) {
                        Swal.fire('Unlocked!', 'The lead has been successfully unlocked.', 'success');
                        fetchLeadDetail(); // Refresh data
                    }
                } catch (err) {
                    Swal.fire('Error!', err.response?.data?.message || "Failed to unlock lead", 'error');
                }
            }
        });
    };

    return (
        <div className="row">
            <div className="col-12">
                <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                    <h4 className="mb-sm-0">Lead Details</h4>
                    <div className="page-title-right">
                        <button className="btn btn-soft-secondary btn-sm" onClick={() => navigate('/admin/my-leads')}>
                            <i className="ri-arrow-left-line align-bottom me-1"></i> Back to List
                        </button>
                    </div>
                </div>
            </div>

            <div className="col-lg-8">
                {isLocked && (
                    <div className="alert alert-warning mb-4 d-flex align-items-center justify-content-between">
                        <div>
                            <strong><i className="ri-lock-2-line align-middle me-1"></i> This lead is locked.</strong>
                            <br />Unlock this lead to view contact details like mobile, email, city, and message. It will consume 1 lead balance.
                        </div>
                        <button className="btn btn-warning" onClick={handleUnlock}>
                            Unlock Lead
                        </button>
                    </div>
                )}
                <div className="card">
                    <div className="card-header align-items-center d-flex">
                        <h4 className="card-title mb-0 flex-grow-1">Lead Information</h4>
                        <div>
                            {statusBadge(vendorLead.status)}
                        </div>
                    </div>
                    <div className="card-body">
                        <div className="row">
                            <div className="col-md-6 mb-3">
                                <label className="text-muted mb-1">Full Name</label>
                                <h5>{lead.name}</h5>
                            </div>
                            <div className="col-md-6 mb-3">
                                <label className="text-muted mb-1">Category</label>
                                <h5 className="text-capitalize">{lead.category.replace('_', ' ')}</h5>
                            </div>
                            <div className="col-md-6 mb-3">
                                <label className="text-muted mb-1">Mobile Number</label>
                                <h5>{lead.mobile}</h5>
                            </div>
                            <div className="col-md-6 mb-3">
                                <label className="text-muted mb-1">Email Address</label>
                                <h5>{lead.email || 'N/A'}</h5>
                            </div>
                            <div className="col-md-6 mb-3">
                                <label className="text-muted mb-1">City</label>
                                <h5>{lead.city || 'N/A'}</h5>
                            </div>
                            <div className="col-md-6 mb-3">
                                <label className="text-muted mb-1">Lead Created On</label>
                                <h5>{new Date(lead.created_at).toLocaleString()}</h5>
                            </div>
                            <div className="col-md-6 mb-3">
                                <label className="text-muted mb-1">Assigned On</label>
                                <h5>{new Date(vendorLead.created_at).toLocaleString()}</h5>
                            </div>
                            {lead.message && (
                                <div className="col-md-12 mb-3">
                                    <label className="text-muted mb-1">Message</label>
                                    <div className="p-3 bg-light rounded" style={{ whiteSpace: 'pre-wrap', border: '1px solid #e9ebec' }}>
                                        {lead.message}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-header">
                        <h4 className="card-title mb-0">Status History & Notes</h4>
                    </div>
                    <div className="card-body">
                        <div className="list-group list-group-flush">
                            {lead.VendorStatusHistories && lead.VendorStatusHistories.length > 0 ? (
                                lead.VendorStatusHistories.map((history, index) => (
                                    <div key={history.id} className="list-group-item px-0 py-3 border-0 border-bottom border-dashed">
                                        <div className="d-flex align-items-start">
                                            <div className="flex-grow-1">
                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                    <h6 className="mb-0">{statusBadge(history.status)}</h6>
                                                    <small className="text-muted">{new Date(history.created_at).toLocaleString()}</small>
                                                </div>
                                                <p className="text-muted mb-0">{history.note || 'No note added.'}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className="text-center text-muted py-3">No status history available.</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="col-lg-4">
                <div className="card">
                    <div className="card-header">
                        <h4 className="card-title mb-0">Update Status</h4>
                    </div>
                    <div className="card-body">
                        <form onSubmit={handleStatusUpdate}>
                            <div className="mb-3">
                                <label className="form-label">New Status</label>
                                <select
                                    className="form-select"
                                    value={newStatus}
                                    onChange={(e) => setNewStatus(e.target.value)}
                                    required
                                >
                                    <option value="open">Open</option>
                                    <option value="follow_up">Follow-up</option>
                                    <option value="closed">Closed</option>
                                    <option value="converted">Converted</option>
                                </select>
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Add Note (Optional)</label>
                                <textarea
                                    className="form-control"
                                    rows="4"
                                    placeholder="Enter status update details..."
                                    value={newNote}
                                    onChange={(e) => setNewNote(e.target.value)}
                                ></textarea>
                            </div>
                            <div className="text-end">
                                <button type="submit" className="btn btn-primary w-100" disabled={updating || isLocked}>
                                    {isLocked ? 'Unlock Lead to Update Status' : (updating ? 'Updating...' : 'Update Lead Status')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default LeadDetail;
